/**
 * WHERE SHE CAME FROM, drawn once for the three screens that ask it: the enquiry panel, the
 * new-client form and the household record.
 *
 * Priority 7 of the business brief is knowing which channel is worth money, and at twenty
 * households the only honest read on that is what somebody typed at the time. So this block is
 * three fields and each of them exists for a counted reason:
 *
 *   THE CHANNEL is a value from `CHANNELS` and never free text, because every kill rule in the
 *   plan counts against one word. Which words are offered is the caller's business: the enquiry
 *   door refuses `website_form` on a row the website did not mint, and the household record does
 *   not, and both of those are decided by the server and merely drawn here.
 *
 *   THE DETAIL is free text with a datalist over what is already stored. Report A groups on that
 *   column, so three spellings of one Nextdoor thread are three rows in the roll-up and a kill
 *   rule applied to a third of the evidence. A picker stops the fourth spelling being invented; a
 *   list that REFUSED a new answer would push the real one into the notes, so it only suggests.
 *
 *   THE INTRODUCER is an id, never a name typed into the detail. "Rosa's neighbour" is a referral
 *   payment that cannot be traced to anybody, and about $107 a household is budgeted against that
 *   channel. One introducer and never two, which the server says first and the table says after.
 *
 * THE CREDIT FOLLOWS THE WORD. Moving the channel off `referral` clears the introducer, because a
 * household credited to somebody under a word that is not a referral is a payment nobody can
 * explain. `sourcePatch` is where that happens, so all three screens do it the same way, and it
 * lives in `web/src/source.ts` with the rest of the rules that need no browser to be true.
 *
 * NOTHING HERE IS CORAL. A source that has not been corrected is not a gap anybody has to close;
 * it is a fact somebody has not needed to change.
 */
import { useEffect, useState, type ReactNode } from 'react';
import type { Channel } from '@fk/shared';
import { ApiError, getClient, listClients, listSourceDetails, type ClientRow } from '../api.ts';
import { CHANNEL_LABELS } from '../labels.ts';
import type { SourceAnswer } from '../source.ts';
import { SelectField, TextField } from './Field.tsx';

/* ------------------------------------------------------------------ *
 * What is already stored, read once for the life of the tab.
 * ------------------------------------------------------------------ */

let detailsRead: Promise<readonly string[]> | null = null;

function sourceDetails(): Promise<readonly string[]> {
  detailsRead ??= listSourceDetails()
    .then((answer) => answer.details)
    .catch(() => {
      // A picker that did not load is a plain box, which is what it was before it existed. The
      // failure is forgotten rather than cached, so the next screen that asks tries again.
      detailsRead = null;
      return [];
    });
  return detailsRead;
}

/**
 * The words already typed, for the datalist. One request however many blocks are on the screen,
 * because the enquiry list draws this inside every card and Workers Free allows fifty subrequests
 * an invocation with every D1 call counting against them.
 */
export function useSourceDetails(): readonly string[] {
  const [details, setDetails] = useState<readonly string[]>([]);
  useEffect(() => {
    let live = true;
    void sourceDetails().then((answer) => {
      if (live) setDetails(answer);
    });
    return () => {
      live = false;
    };
  }, []);
  return details;
}

/* ------------------------------------------------------------------ *
 * Who introduced her.
 * ------------------------------------------------------------------ */

/** Names read for an id, kept for the life of the tab: the same introducer names many rows. */
const nameById = new Map<string, string>();

/**
 * The introducer's name for an id.
 *
 * Three answers in order of cost and none of them optional: the name the screen already has, the
 * name somebody just pressed in this tab, and, only then, the household read for the one word,
 * because the enquiry list does not carry it. A name that cannot be read leaves the credit
 * standing and says so in words rather than showing an id nobody can recognise.
 */
function useClientName(id: string | null, known: string | null | undefined): string | null {
  const [read, setRead] = useState<{ id: string; name: string } | null>(null);
  const already = id ? (known ?? nameById.get(id) ?? null) : null;

  useEffect(() => {
    if (!id) return;
    if (known) {
      nameById.set(id, known);
      return;
    }
    if (nameById.has(id)) return;
    let live = true;
    getClient(id)
      .then((record) => {
        nameById.set(id, record.client.displayName);
        if (live) setRead({ id, name: record.client.displayName });
      })
      .catch(() => {
        // The credit is on the row either way. A block that refused to draw because one name did
        // not load would hide the thing it is about.
      });
    return () => {
      live = false;
    };
  }, [id, known]);

  if (already) return already;
  return read && read.id === id ? read.name : null;
}

/**
 * One household out of the client book, chosen and never typed. Search, press a name, and it is
 * shown as a name from then on with a control that hands it back to the search box.
 */
export function ReferrerPicker({
  idPrefix,
  value,
  name,
  onChange,
}: {
  idPrefix: string;
  value: string | null;
  /** The name where the screen already has it, so the picker asks the server for nothing. */
  name?: string | null;
  onChange: (id: string | null, name: string | null) => void;
}) {
  const chosen = useClientName(value, name);
  const [search, setSearch] = useState('');
  const [asked, setAsked] = useState('');
  const [rows, setRows] = useState<readonly ClientRow[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const id = `${idPrefix}-referrer`;

  /** Typing is not a request. A quarter second of quiet is, which is a lot less mobile data. */
  useEffect(() => {
    const timer = setTimeout(() => setAsked(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (asked.length < 2) {
      setRows(null);
      return;
    }
    let live = true;
    setFailed(null);
    listClients(asked)
      .then((answer) => {
        if (live) setRows(answer.clients);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setFailed(thrown instanceof ApiError ? thrown.message : 'The client book did not answer.');
      });
    return () => {
      live = false;
    };
  }, [asked]);

  if (value) {
    return (
      <div className="mb-4">
        <span className="block text-sm font-medium">Who introduced them</span>
        <div className="mt-1 flex flex-wrap items-center gap-2 rounded-lg border border-navy/25 bg-parchment px-3 py-2">
          <span className="min-w-0 grow text-sm font-medium">
            {chosen ?? 'A household in the book'}
          </span>
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setAsked('');
              onChange(null, null);
            }}
            className="inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border border-navy/25 px-3 font-medium"
          >
            <span className="text-sm">Somebody else</span>
          </button>
        </div>
        <p className="mt-1 text-xs text-navy/65">
          One introducer, never two. The thank-you is a call somebody makes, and it is owed to one
          household.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-medium">
        Who introduced them
      </label>
      <input
        id={id}
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search the client book"
        autoComplete="off"
        aria-describedby={`${id}-hint`}
        className="mt-1 block min-h-11 desk:min-h-9 w-full rounded-lg border border-navy/25 bg-parchment px-3 py-2 text-navy"
      />
      <p id={`${id}-hint`} className="mt-1 text-xs text-navy/65">
        A household, chosen out of the book and never typed. Two letters is enough to search. A name
        typed into the words above is a referral nobody can trace or pay.
      </p>

      {failed ? (
        <p className="mt-2 text-sm" role="alert">
          {failed}
        </p>
      ) : rows === null ? null : rows.length === 0 ? (
        <p className="mt-2 text-sm text-navy/70">
          Nobody in the book matches that. They may be a name outside it, and then the words above
          are where they go for now.
        </p>
      ) : (
        <ul className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-navy/15">
          {rows.slice(0, 12).map((row) => (
            <li key={row.id} className="border-b border-navy/10 last:border-b-0">
              <button
                type="button"
                onClick={() => {
                  // The name is in hand here, so it is kept: without this the block would read
                  // the household back for the one word it just had under the finger.
                  nameById.set(row.id, row.displayName);
                  onChange(row.id, row.displayName);
                }}
                className="flex min-h-11 desk:min-h-9 w-full flex-col justify-center px-3 py-1.5 text-left"
              >
                <span className="text-sm font-medium">{row.displayName}</span>
                {row.sourceDetail ? (
                  <span className="truncate text-xs text-navy/65">{row.sourceDetail}</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The block itself.
 * ------------------------------------------------------------------ */

/**
 * The sentence under `website_form` where it can be chosen at all. A household typed in by hand
 * has no lead id to prove it, so the word is offered and explained rather than refused: filing her
 * as `other` corrupts the exact count priority 7 exists to produce.
 */
const WEBSITE_FORM_NOTE =
  'A website lead usually arrives on its own; type one only if the form was down.';

export function SourceFields({
  idPrefix,
  channels,
  value,
  onChange,
  channelLabel = 'How they heard about us',
  channelHint,
  channelPlaceholder,
  detailLabel = 'Which one, exactly',
  detailHint = "Which Nextdoor thread, which realtor's office, which letter batch.",
  referrer,
  required,
  websiteFormNote = WEBSITE_FORM_NOTE,
}: {
  /** Where this copy is drawn, because a list can hold many of these at once. */
  idPrefix: string;
  /** The words this screen may offer. The server refuses the rest and says which and why. */
  channels: readonly Channel[];
  value: SourceAnswer;
  onChange: (next: SourceAnswer) => void;
  channelLabel?: string;
  channelHint?: ReactNode;
  /** Given, the field starts empty and somebody has to choose. Omitted, it holds what it holds. */
  channelPlaceholder?: string;
  detailLabel?: string;
  detailHint?: ReactNode;
  /**
   * The introducer this screen already has a name for, as a PAIR. A bare name would be read
   * against whatever id the picker is holding, so choosing a different household would show the
   * previous one's name over the new one's id until a request came back and said otherwise.
   */
  referrer?: { readonly id: string; readonly name: string } | null;
  required?: boolean;
  websiteFormNote?: ReactNode;
}) {
  const details = useSourceDetails();
  const listId = `${idPrefix}-source-details`;

  // A select whose value is not among its options draws blank, and a save would then send a word
  // nobody chose. So the word the row already wears is drawn either way: as a choice where this
  // screen may offer it, and read only where it may not, which is a fact rather than a blank.
  const wearing = value.sourceChannel === '' ? null : value.sourceChannel;
  const locked = wearing !== null && !channels.includes(wearing) ? wearing : null;

  return (
    <div>
      {locked ? (
        <div className="mb-4">
          <span className="block text-sm font-medium">{channelLabel}</span>
          <p className="mt-1 min-h-11 desk:min-h-9 rounded-lg border border-navy/15 bg-navy/5 px-3 py-2.5 text-navy">
            {CHANNEL_LABELS[locked]}
          </p>
          <p className="mt-1 text-xs text-navy/65">
            This one arrived signed from the website, so it is not a word anybody types here.
          </p>
        </div>
      ) : (
        <SelectField
          id={`${idPrefix}-source-channel`}
          label={channelLabel}
          required={required}
          value={value.sourceChannel}
          onChange={(channel) => onChange({ ...value, sourceChannel: channel })}
          options={channels}
          toLabel={(channel) => CHANNEL_LABELS[channel]}
          placeholder={channelPlaceholder}
          hint={channelHint}
        />
      )}

      {locked === null && value.sourceChannel === 'website_form' ? (
        <p className="-mt-2 mb-4 text-xs text-navy/70">{websiteFormNote}</p>
      ) : null}

      <TextField
        id={`${idPrefix}-source-detail`}
        label={detailLabel}
        value={value.sourceDetail}
        onChange={(detail) => onChange({ ...value, sourceDetail: detail })}
        hint={detailHint}
        autoComplete="off"
        list={listId}
      />
      <datalist id={listId}>
        {details.map((detail) => (
          <option key={detail} value={detail} />
        ))}
      </datalist>

      {value.sourceChannel === 'referral' ? (
        <ReferrerPicker
          idPrefix={idPrefix}
          value={value.referredByClientId}
          name={referrer && referrer.id === value.referredByClientId ? referrer.name : null}
          onChange={(id) => onChange({ ...value, referredByClientId: id })}
        />
      ) : null}
    </div>
  );
}
